import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  UserIcon,
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  BoltIcon,
  CpuChipIcon,
  CalendarIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';

interface CompanionDetailsData {
  companion: {
    id: string;
    name: string;
    gender: string;
    personality: string;
    systemPrompt: string;
    avatarUrl: string | null;
    isActive: boolean;
    isDefault: boolean;
    createdAt: string;
    updatedAt: string;
  };
  owner: {
    id: string;
    email: string;
    name: string;
    username: string;
  };
  traits: {
    template: string;
    moodState: string;
    personality: {
      openness: number;
      conscientiousness: number;
      extraversion: number;
      agreeableness: number;
      neuroticism: number;
    };
    communicationStyle: any;
    knowledgeDomains: string[];
    adaptationCount: number;
    lastUserSatisfaction: number;
    createdAt: string;
    updatedAt: string;
  } | null;
  stats: {
    conversations: {
      total: number;
      totalMessages: number;
      avgMessagesPerConversation: number;
      maxRelationshipLevel: number;
      avgRelationshipLevel: number;
    };
    messages: {
      byRole: { [key: string]: number };
      flaggedCount: number;
    };
    tokens: {
      totalPromptTokens: number;
      totalCompletionTokens: number;
      totalTokens: number;
      totalCreditsUsed: number;
    };
  };
  recentConversations: Array<{
    id: string;
    userId: string;
    userName: string;
    messageCount: number;
    relationshipLevel: number;
    lastMessageAt: string;
    createdAt: string;
  }>;
}

export default function CompanionDetails() {
  const { id } = useParams<{ id: string }>();
  const [companion, setCompanion] = useState<CompanionDetailsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCompanionDetails = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`/admin/companions/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompanion(response.data);
    } catch (err) {
      setError('Failed to load companion details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchCompanionDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 p-4">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600">Loading companion details...</div>
        </div>
      </div>
    );
  }

  if (error || !companion) {
    return (
      <div className="min-h-screen bg-gray-100 p-4">
        <div className="flex items-center justify-center h-64">
          <div className="text-red-600">{error || 'Companion not found'}</div>
        </div>
      </div>
    );
  }

  const getTraitColor = (value: number) => {
    if (value >= 75) return 'bg-green-500';
    if (value >= 50) return 'bg-blue-500';
    if (value >= 25) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <Link
            to="/admin/users"
            className="inline-flex items-center text-purple-600 hover:text-purple-700 mb-4"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Users
          </Link>

          <div className="flex items-start justify-between">
            <div className="flex items-center">
              {companion.companion.avatarUrl ? (
                <img
                  src={companion.companion.avatarUrl}
                  alt={companion.companion.name}
                  className="w-16 h-16 rounded-full mr-4"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center mr-4">
                  <UserIcon className="w-8 h-8 text-gray-400" />
                </div>
              )}
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{companion.companion.name}</h1>
                <p className="text-sm text-gray-600 mt-1">
                  {companion.companion.gender} • {companion.traits?.template || 'No template'}
                  {companion.companion.isDefault && (
                    <span className="ml-2 px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">
                      Default
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={`px-3 py-1 text-xs font-medium rounded-full ${
                  companion.companion.isActive
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {companion.companion.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </div>

        {/* Owner Info */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <UserIcon className="w-5 h-5 text-gray-400 mr-2" />
            Owner Information
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">Name</dt>
              <dd className="mt-1 text-sm text-gray-900">{companion.owner.name || 'N/A'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Email</dt>
              <dd className="mt-1 text-sm">
                <Link
                  to={`/admin/users/${companion.owner.id}`}
                  className="text-purple-600 hover:text-purple-700"
                >
                  {companion.owner.email}
                </Link>
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Username</dt>
              <dd className="mt-1 text-sm text-gray-900">{companion.owner.username || 'N/A'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">User ID</dt>
              <dd className="mt-1 text-xs text-gray-900 font-mono">
                {companion.owner.id.substring(0, 8)}...
              </dd>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {/* Conversations */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <ChatBubbleLeftRightIcon className="w-5 h-5 text-gray-400 mr-2" />
              Conversations
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-500">Total</span>
                <span className="text-gray-900 font-medium">
                  {companion.stats.conversations.total}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Total Messages</span>
                <span className="text-gray-900 font-medium">
                  {companion.stats.conversations.totalMessages}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Avg Messages/Conv</span>
                <span className="text-gray-900 font-medium">
                  {companion.stats.conversations.avgMessagesPerConversation.toFixed(1)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Max Relationship</span>
                <span className="text-gray-900 font-medium">
                  {companion.stats.conversations.maxRelationshipLevel}/10
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Avg Relationship</span>
                <span className="text-gray-900 font-medium">
                  {companion.stats.conversations.avgRelationshipLevel.toFixed(1)}/10
                </span>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <ChartBarIcon className="w-5 h-5 text-gray-400 mr-2" />
              Messages
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-500">User Messages</span>
                <span className="text-gray-900 font-medium">
                  {companion.stats.messages.byRole.user || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">AI Messages</span>
                <span className="text-gray-900 font-medium">
                  {companion.stats.messages.byRole.assistant || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">System Messages</span>
                <span className="text-gray-900 font-medium">
                  {companion.stats.messages.byRole.system || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Flagged</span>
                <span className="text-red-600 font-medium">
                  {companion.stats.messages.flaggedCount}
                </span>
              </div>
            </div>
          </div>

          {/* Tokens */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <BoltIcon className="w-5 h-5 text-gray-400 mr-2" />
              Token Usage
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-500">Prompt Tokens</span>
                <span className="text-gray-900 font-medium">
                  {companion.stats.tokens.totalPromptTokens.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Completion Tokens</span>
                <span className="text-gray-900 font-medium">
                  {companion.stats.tokens.totalCompletionTokens.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Total Tokens</span>
                <span className="text-gray-900 font-medium">
                  {companion.stats.tokens.totalTokens.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Credits Used</span>
                <span className="text-purple-600 font-medium">
                  {companion.stats.tokens.totalCreditsUsed.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Personality Traits */}
        {companion.traits && (
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <CpuChipIcon className="w-5 h-5 text-gray-400 mr-2" />
              Personality Traits
            </h2>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <dt className="text-sm font-medium text-gray-500">Template</dt>
                <dd className="mt-1 text-sm text-gray-900">{companion.traits.template}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Mood State</dt>
                <dd className="mt-1 text-sm text-gray-900">{companion.traits.moodState}</dd>
              </div>
            </div>

            <div className="space-y-4">
              {Object.entries(companion.traits.personality).map(([trait, value]) => (
                <div key={trait}>
                  <div className="flex justify-between mb-1">
                    <span className="text-gray-500 capitalize">{trait}</span>
                    <span className="text-gray-900">{value}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${getTraitColor(value)}`}
                      style={{ width: `${value}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6">
              <dt className="text-sm font-medium text-gray-500 mb-2">Knowledge Domains</dt>
              <div className="flex flex-wrap gap-2">
                {companion.traits.knowledgeDomains.map((domain) => (
                  <span
                    key={domain}
                    className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm"
                  >
                    {domain}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Adaptation Count</dt>
                <dd className="mt-1 text-sm text-gray-900">{companion.traits.adaptationCount}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Last User Satisfaction</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {companion.traits.lastUserSatisfaction.toFixed(1)}
                </dd>
              </div>
            </div>
          </div>
        )}

        {/* Recent Conversations */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <CalendarIcon className="w-5 h-5 text-gray-400 mr-2" />
            Recent Conversations
          </h2>

          {companion.recentConversations.length > 0 ? (
            <div className="space-y-3">
              {companion.recentConversations.map((conv) => (
                <div
                  key={conv.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex-1">
                    <Link
                      to={`/admin/conversations/${conv.id}`}
                      className="text-purple-600 hover:text-purple-700 font-medium"
                    >
                      {conv.userName}
                    </Link>
                    <div className="text-sm text-gray-500 mt-1">
                      {conv.messageCount} messages • Level {conv.relationshipLevel}
                    </div>
                  </div>
                  <div className="text-right text-sm text-gray-500">
                    <div>{new Date(conv.lastMessageAt).toLocaleDateString()}</div>
                    <div>{new Date(conv.lastMessageAt).toLocaleTimeString()}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No conversations yet</p>
          )}
        </div>

        {/* Full System Prompt */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <CpuChipIcon className="w-5 h-5 text-gray-400 mr-2" />
            System Prompt
          </h2>
          <div className="bg-gray-50 rounded-lg p-4 border">
            <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono leading-relaxed">
              {companion.companion.systemPrompt || 'No system prompt available'}
            </pre>
          </div>
        </div>

        {/* System Information */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <ShieldCheckIcon className="w-5 h-5 text-gray-400 mr-2" />
            System Information
          </h2>
          <dl className="grid grid-cols-1 gap-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">Companion ID</dt>
              <dd className="mt-1 text-sm text-gray-900 font-mono">{companion.companion.id}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">User Description</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {companion.companion.personality || 'No description provided'}
              </dd>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Created</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(companion.companion.createdAt).toLocaleString()}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(companion.companion.updatedAt).toLocaleString()}
                </dd>
              </div>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
}
